import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { VentasService } from '../services/ventas.service';
import { GenericTableHandlerService } from '../shared/GenericTableHandlerService.service';
import { VentasDialogComponent } from './ventas-dialog/ventas-dialog.component';
import { Sort } from '@angular/material/sort';
import { AuthService } from '../services/auth.service';
import { take } from 'rxjs/operators';
import { TableAction } from '../table-generic/table-generic.component';
import { MetodoPago, METODO_PAGO_LABELS } from '../enums/metodo-pago.enum';

@Component({
  selector: 'app-ventas',
  templateUrl: './ventas.component.html',
  styleUrls: ['./ventas.component.scss']
})
export class VentasComponent extends GenericTableHandlerService implements OnInit {
  @Output() detailsClick = new EventEmitter<any>();
  isAdministrador = false;
  MetodoPago = MetodoPago;
  metodosPagoFiltro = [
    { value: null, label: 'Todos' },
    { value: MetodoPago.EFECTIVO, label: METODO_PAGO_LABELS[MetodoPago.EFECTIVO] },
    { value: MetodoPago.TARJETA, label: METODO_PAGO_LABELS[MetodoPago.TARJETA] },
    { value: MetodoPago.TRANSFERENCIA, label: METODO_PAGO_LABELS[MetodoPago.TRANSFERENCIA] },
  ];
  metodoPagoSeleccionado: MetodoPago | null = null;

  constructor(
     ventaService: VentasService,
     override dialog: MatDialog,
     override toastr: ToastrService,
     private authService: AuthService
  ) {
    super(ventaService, dialog, toastr);
  }

  ngOnInit(): void {
    // Verificar primero si el usuario ya está disponible en el snapshot
    const currentUser = this.authService.getCurrentUserSnapshot();
    
    if (currentUser?.['rol']) {
      // Usuario ya disponible, verificar rol y inicializar
      const rolNombre = typeof currentUser['rol'] === 'string' 
        ? currentUser['rol'] 
        : currentUser['rol']?.['rol'];
      
      this.isAdministrador = rolNombre === 'Administrador';
      this.initializeTable();
    } else {
      // Usuario no disponible aún, esperar a que esté disponible
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        // Verificar el rol del usuario
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string' 
            ? user['rol'] 
            : user['rol']?.['rol'];
          
          this.isAdministrador = rolNombre === 'Administrador';
        }
        
        // Inicializar la tabla con las acciones correctas
        this.initializeTable();
      });
    }
  }

  private initializeTable(): void {
    // Construir acciones dinámicamente según el rol
    const acciones: TableAction[] = [
      { icon: 'visibility', tooltip: 'Ver detalles', emit: 'detailsClick' }
    ];

    // Agregar acción de eliminar solo si es administrador
    if (this.isAdministrador) {
      acciones.push({ 
        icon: 'delete', 
        tooltip: 'Eliminar venta', 
        color: 'warn',
        emit: 'deleteClick' 
      });
    }

    this.initialize(
      {
        title: 'Historial de Ventas',
        selectable: false,
        stickyHeader: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 100],
        noDataMessage: 'No hay ventas registradas',
        columns: [
        { key: 'id', title: 'ID', sortable: true, width: '80px',align: 'center'  // Añadir alineación
        },
        {
          key: 'createdAt',title: 'Fecha', type: 'date', sortable: true,format: 'dd/MM/yyyy HH:mm' // Especificar formato
        },
          { key: 'usuario.name', title: 'Vendedor', sortable: true },
          { key: 'total', title: 'Total', type: 'currency', sortable: true },
          { key: 'estado', title: 'Estado', sortable: true,
            badge: {
              'completada': 'success',
              'pendiente': 'warning',
              'cancelada': 'danger'
            }
          },
          {
            key: 'actions',
            title: 'Acciones',
            type: 'actions',
            actions: acciones
          }
        ]
      },
      null,
      ''
    );

    this.loadData();
  }

  override loadData(extraParams: Record<string, any> = {}): void {
    const params = { ...extraParams };
    if (this.metodoPagoSeleccionado) {
      params['metodoPago'] = this.metodoPagoSeleccionado;
    }
    super.loadData(params);
  }

  filtrarPorMetodoPago(metodoPago: MetodoPago | null): void {
    this.metodoPagoSeleccionado = metodoPago;
    this.currentPage$.next(0);
    this.loadData();
  }

  onViewDetails(venta: any): void {
    this.detailsClick.emit(venta); // Emitir primero el evento
    this.dialog.open(VentasDialogComponent, {
      width: '800px',
      data: { venta: venta }
    });
  }

  override onSortChange(sort: Sort): void {
    this.currentSort$.next({
      key: sort.active,
      direction: (sort.direction || 'asc') as 'asc' | 'desc'
    });
    this.loadData();
  }
  
  onEdit(venta: any): void {
    // Opcional: Si necesitas editar ventas
    console.log('Editar venta', venta);
  }

  abrirDialogoReporte(venta?: any): void {
    const dialogRef = this.dialog.open(VentasDialogComponent, {
      width: '600px',
      data: {
        venta: venta ? {...venta} : undefined,
        isEdit: venta,
        ventaId: venta?.id
      }
    });
    dialogRef.afterClosed().subscribe((success: boolean) => {
      console.log('success', success)
      if (success) {
        this.loadData(); // Recargar datos solo si fue exitoso
      }
    });
  }


  onDelete(venta: any): void {
    if (!venta || !venta.id) {
      this.toastr.warning('No se puede eliminar esta venta');
      return;
    }

    // Confirmar eliminación
    const confirmMessage = `¿Está seguro de que desea eliminar la venta #${venta.id}?`;
    if (confirm(confirmMessage)) {
      this.loading$.next(true);
      // Usar el método delete del BaseService pasando el ID como path
      // El BaseService ya tiene el endpoint 'v1/ventas' configurado
      this.baseService.delete(venta.id.toString()).subscribe({
        next: () => {
          this.toastr.success('Venta eliminada exitosamente');
          this.loadData();
          this.loading$.next(false);
        },
        error: (error) => {
          const errorMessage = error?.error?.message || error?.message || 'Error al eliminar la venta';
          this.toastr.error(errorMessage);
          this.loading$.next(false);
        }
      });
    }
  }
}
